import { motion } from 'framer-motion';
import { Wallet, TrendingUp, Shield, RefreshCw, ExternalLink, Trophy, FileText } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import TokenDistributionChart from '../components/TokenDistributionChart';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';

const TokenPage = () => {
  return (
    <>
      <PageHero
        eyebrow="Tokenization"
        title="SFUT Token"
        subtitle="The SFUT token is the backbone of the SportsFuture ecosystem, facilitating transactions and governance."
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-5xl mx-auto"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold mb-6">Token Overview</h2>
            <p className="text-gray-700 mb-6">
              The SportsFuture (SFUT) token powers our ecosystem, creating a decentralized economy that connects athletes with supporters worldwide. By leveraging blockchain technology, we ensure transparent, efficient transactions that directly benefit talented individuals.
            </p>
            <p className="text-gray-700 mb-6">
              SFUT is built on the Apertum blockchain, which provides high security, scalability, and interoperability with other blockchain networks. This foundation enables us to create a robust platform for sports funding that can grow with our community.
            </p>
            <p className="text-gray-700 mb-6">
              Through our platform, token holders can participate in governance decisions, earn staking rewards, and access exclusive features as our ecosystem expands.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                as="a"
                href="https://dex.apertum.io/#/token/0xa8532d029320ad0176efd0959e044aa610a751ed"
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
                className="inline-flex items-center"
              >
                View on Apertum DEX
                <ExternalLink size={16} className="ml-2" />
              </Button>
              <Button
                as="a"
                href="https://drive.google.com/file/d/1r3qKyY-i0KRTLGCsszP2PF0l9MhsYDn7/view"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                className="inline-flex items-center"
              >
                SFUT Litepaper
                <FileText size={16} className="ml-2" />
              </Button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg sf-surface-card sf-ui-system p-6"
          >
            <h3 className="text-xl font-semibold mb-6 text-center">Token Distribution</h3>
            <TokenDistributionChart />
          </motion.div>
        </div>
        
        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">Key Token Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Governance</h3>
              <p className="text-gray-600 text-center">
                Vote on funding decisions, platform upgrades, and other key initiatives.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                  <TrendingUp className="h-6 w-6" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Staking Rewards</h3>
              <p className="text-gray-600 text-center">
                Earn returns by staking your tokens to support platform security.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                  <Shield className="h-6 w-6" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Utility</h3>
              <p className="text-gray-600 text-center">
                Access exclusive features, events, and athlete support opportunities.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                  <RefreshCw className="h-6 w-6" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Liquidity</h3>
              <p className="text-gray-600 text-center">
                Easily tradable on the Apertum DEX with multiple trading pairs.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col justify-center"
          >
            <h2 className="text-3xl font-bold mb-4">Join Our Liquidity Pool</h2>
            <p className="text-lg text-gray-700 mb-6">
              Become a founding stakeholder in sports funding's future. By joining our liquidity pool, 
              you'll be directly supporting athletes while earning rewards in the process.
            </p>
            <div>
              <Button to="/faq" variant="primary">
                Learn More
                <ExternalLink size={16} className="ml-1" />
              </Button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center items-center"
          >
            <div className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system w-full max-w-md">
              <div className="flex items-center justify-center mb-6">
                <Trophy className="text-primary w-12 h-12" />
              </div>
              <h3 className="text-xl font-semibold text-center mb-4">SFUT Token Benefits</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2 text-gray-600">Governance voting rights</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2 text-gray-600">Staking rewards</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2 text-gray-600">Access to exclusive athlete events</p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="ml-2 text-gray-600">Early access to new features</p>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
        
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10">
              <h2 className="text-2xl font-bold text-white mb-6">Token Economics</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="font-semibold mb-1">Total Supply</h3>
                  <p>8,212,557,050 SFUT</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Initial Circulating Supply</h3>
                  <p>6,421,135.92 SFUT (20%)</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Token Type</h3>
                  <p>Utility & Governance</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Blockchain</h3>
                  <p>Apertum</p>
                </div>
              </div>
            </div>
            
            <div className="bg-primary-900 p-10">
              <h2 className="text-2xl font-bold text-white mb-6">How to Participate</h2>
              <ol className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold mr-3">1</span>
                  <div>
                    <h3 className="font-semibold mb-1">Create an Apertum Wallet</h3>
                    <p className="text-sm">Download the official Apertum wallet app and create your account.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold mr-3">2</span>
                  <div>
                    <h3 className="font-semibold mb-1">Connect to Apertum DEX</h3>
                    <p className="text-sm">Access the Apertum decentralized exchange through your wallet.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold mr-3">3</span>
                  <div>
                    <h3 className="font-semibold mb-1">Start Trading</h3>
                    <p className="text-sm">Begin trading SFUT tokens on the Apertum DEX.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold mr-3">4</span>
                  <div>
                    <h3 className="font-semibold mb-1">Participate in Governance</h3>
                    <p className="text-sm">Use your SFUT tokens to vote on proposals and shape the platform's future.</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
        
        <div className="mt-16 bg-primary-50 rounded-lg p-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-4">Ready to Join Our Ecosystem?</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Become a founding stakeholder in the future of sports funding. Join our community of supporters
              and help talented athletes achieve their dreams.
            </p>
            <Button to="/faq" variant="outline" size="lg">
              Learn More
            </Button>
          </motion.div>
        </div>
      </motion.div>
      </Section>
    </>
  );
};

export default TokenPage;